import type { DocPage } from '../types'
import { media } from '../media'

export const modelTools: DocPage = {
  id: 'model-tools',
  title: 'Model tree & filters',
  description:
    'Browse collections and attributes, use Feature Options, and limit what loads with the Filters modal. Map tools are documented separately under Map view.',
  category: 'Workspace',
  sections: [
    { id: 'tree', title: 'Model tree' },
    { id: 'search', title: 'Search and selection' },
    { id: 'feature-options', title: 'Feature Options' },
    { id: 'filters', title: 'Filters modal' },
    { id: 'logical-pointer', title: 'Logical objects' },
    { id: 'map-pointer', title: 'Map view' },
  ],
  blocks: [
    {
      type: 'heading',
      level: 2,
      id: 'tree',
      text: 'Model tree',
    },
    {
      type: 'paragraph',
      text: 'After you connect a format, X-Translator loads a hierarchical model of collections (feature classes / layers / tables) and their attributes (fields). Expand a collection to see attributes, types, and optional feature counts. Source and Target each have their own tree after a successful model load.',
    },
    {
      type: 'figure',
      src: media.sourceModelTreePostgis,
      alt: 'Source model tree for PostGIS',
      caption:
        'Figure: Source model tree after a PostGIS connection — expand collections to see attributes before mapping.',
    },
    {
      type: 'list',
      items: [
        'Collections are the units you Connect for mapped transfers.',
        'Attributes are fields you Connect under a collection mapping.',
        'Logical collections/attributes appear alongside physical ones when defined.',
        'CAD trees reflect Model Mode (Translator / Layer / Entity) — see AutoCAD.',
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'search',
      text: 'Search and selection',
    },
    {
      type: 'paragraph',
      text: 'Use the search box above the tree to find collections and attributes by name. Multi-select collections when preparing Auto Map — Selected Collections scope. Selection also drives Connect, Disconnect, and context menus for logical objects.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'feature-options',
      text: 'Feature Options',
    },
    {
      type: 'table',
      headers: ['Option', 'Effect'],
      rows: [
        [
          'Show Feature Counts',
          'Display record counts on collections when the format/backend supports counting.',
        ],
        [
          'Hide Feature with 0 records',
          'Hide empty collections from the tree to reduce clutter.',
        ],
        [
          'Show Collection Details',
          'Reveal additional collection metadata in the tree UI when available.',
        ],
      ],
    },
    {
      type: 'callout',
      variant: 'tip',
      title: 'Performance',
      text: 'On very large PostGIS or CAD models, leaving Show Feature Counts off can speed up model load. Use Filters to reduce collections before counting.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'filters',
      text: 'Filters modal',
    },
    {
      type: 'paragraph',
      text: 'Open Add Filter or Change Filters to limit which collections and attributes participate in the model and transfer. The modal has Collections and Attributes tabs.',
    },
    {
      type: 'figure',
      src: media.modelFilter,
      alt: 'Model Filter modal',
      caption:
        'Figure: Model Filter dialog — Collections and Attributes tabs to limit what loads into the model tree and transfer.',
      size: 'compact',
    },
    {
      type: 'steps',
      items: [
        'Click Add Filter / Change Filters on Source or Target (where offered).',
        'On the Collections tab, search, expand/collapse, and select the collections to include.',
        'On the Attributes tab, choose which attributes to include per collection.',
        'Confirm — the model tree refreshes to the filtered subset.',
        'Save the model configuration if you want those filters reused later.',
      ],
    },
    {
      type: 'list',
      items: [
        'Filters reduce what appears in the model and what participates in transfer.',
        'Combine with Map layer visibility: hidden map layers are also excluded from transfer.',
        'After schema changes upstream, reopen Filters to re-check selections.',
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'logical-pointer',
      text: 'Logical objects',
    },
    {
      type: 'paragraph',
      text: 'Context menus on the tree support Add/Update/Remove logical collection, attribute, and enumerator. Full field lists, ENUM rules, and PostGIS-only enumerator notes are in Logical objects.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'map-pointer',
      text: 'Map view',
    },
    {
      type: 'paragraph',
      text: 'Spatial overview, draw tools (Rectangle / Polygon / Freehand / Radius), inclusion modes (Clip / Intersect / Within), layers, basemap, Override EPSG for CAD, sampling messages, and the Attribute Table are documented under Map view & attribute table.',
    },
  ],
}
